import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Calendar, Receipt, ClipboardList, LogOut, Phone, ExternalLink, FileText, Download } from "lucide-react";
import { clientApi, useClientAuth } from "@/context/ClientAuth";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

const PlaceholderCard = ({ icon: Icon, title, body, testId }) => (
  <div data-testid={testId} className="border border-[#E5E7EB] bg-white p-6 flex flex-col">
    <Icon size={26} className="text-[#D94833]" />
    <h3 className="font-head text-xl font-bold mt-4">{title}</h3>
    <p className="text-sm text-[#3A4A63] mt-2 leading-relaxed flex-1">{body}</p>
    <span className="mt-4 inline-block text-[10px] font-bold uppercase tracking-[0.15em] text-[#9CA3AF] border border-[#E5E7EB] px-2 py-1 self-start">
      Nothing yet
    </span>
  </div>
);

const DataCard = ({ icon: Icon, title, testId, children }) => (
  <div data-testid={testId} className="border border-[#E5E7EB] bg-white p-6 flex flex-col">
    <Icon size={26} className="text-[#D94833]" />
    <h3 className="font-head text-xl font-bold mt-4">{title}</h3>
    <div className="mt-3 space-y-3 flex-1">{children}</div>
  </div>
);

const PhoneCapture = ({ onSaved }) => {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await clientApi.put("/client/phone", { phone });
      onSaved(res.data);
      toast.success("Thanks! Your details are saved.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save your phone number.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#041C3E] flex items-center justify-center px-6" data-testid="client-phone-capture">
      <motion.form
        onSubmit={save}
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-white text-[#041C3E] p-10"
      >
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]"><Phone size={14} /> One last step</div>
        <h1 className="font-head text-2xl font-black mt-3">Add your phone number</h1>
        <p className="text-sm text-[#3A4A63] mt-2 leading-relaxed">
          So Rhea can reach you about your sessions. This stays private and is only used to contact you.
        </p>
        <input
          data-testid="client-phone-input"
          required minLength={5}
          placeholder="e.g. +91 98765 43210"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="mt-6 w-full border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-4 py-3 text-sm"
        />
        <button data-testid="client-phone-save" type="submit" disabled={busy}
          className="mt-5 w-full bg-[#041C3E] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#D94833] transition-colors disabled:opacity-50">
          {busy ? "Saving…" : "Save and continue"}
        </button>
      </motion.form>
    </div>
  );
};

const TaxCertRequest = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ aadhaar: "", pan: "", full_name: "", address: "" });

  const load = () => clientApi.get("/client/tax-certificate-requests").then((r) => setRequests(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const titleCase = (s) => (s || "").replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  const looksGibberishAddress = (a) => {
    const s = (a || "").trim();
    if (s.length < 10) return true;
    const words = s.split(/\s+/);
    if (words.length < 2) return true;
    if (words.some((w) => w.replace(/[^A-Za-z]/g, "").length > 20)) return true;
    if (!(/\d/.test(s) || s.includes(",") || words.length >= 3)) return true;
    return false;
  };

  const submit = async (e) => {
    e.preventDefault();
    const cleanName = titleCase(form.full_name.trim());
    if (cleanName.length < 2) { toast.error("Please enter your full name as it should appear on the certificate."); return; }
    if (looksGibberishAddress(form.address)) {
      toast.error("Please enter a complete postal address (include your area and city, and a house or pincode number where possible).");
      return;
    }
    setBusy(true);
    try {
      await clientApi.post("/client/tax-certificate-request", {
        aadhaar: form.aadhaar.replace(/[\s-]/g, ""),
        pan: form.pan.trim().toUpperCase(),
        full_name: cleanName,
        address: form.address.trim(),
      });
      toast.success("Your 80G certificate is ready to download.");
      setOpen(false);
      setForm({ aadhaar: "", pan: "", full_name: "", address: "" });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const download = async (rid) => {
    setDownloading(true);
    try {
      const res = await clientApi.get(`/client/tax-certificate/${rid}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `80g-certificate-${rid.split("-")[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the certificate. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const latest = requests[0];

  return (
    <div className="mt-6 border border-[#E5E7EB] bg-white p-6" data-testid="tax-cert-section">
      <div className="flex items-start gap-3">
        <FileText size={26} className="text-[#D94833]" />
        <div className="flex-1">
          <h3 className="font-head text-xl font-bold">80G tax certificate</h3>
          <p className="text-sm text-[#3A4A63] mt-1 leading-relaxed">
            Your donations to {`KG Strategic Knowledge Forum`} are eligible for tax exemption under Section 80G.
            Request your 80G certificate and download it instantly.
          </p>
        </div>
        {!open && (
          <button data-testid="tax-cert-open" onClick={() => setOpen(true)}
            className="shrink-0 bg-[#041C3E] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#D94833] transition-colors">
            {latest ? "New request" : "Request 80G certificate"}
          </button>
        )}
      </div>

      {latest && !open && (
        <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="tax-cert-ready">
          <button
            data-testid="tax-cert-download"
            onClick={() => download(latest.id)}
            disabled={downloading}
            className="inline-flex items-center gap-2 bg-[#D94833] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#C03F2A] transition-colors disabled:opacity-60"
          >
            <Download size={16} />
            {downloading ? "Preparing…" : "Download your 80G certificate"}
          </button>
          <span className="text-xs text-[#9CA3AF]">
            Aadhaar ••••{(latest.aadhaar || "").slice(-4)}{latest.pan ? ` · PAN ${latest.pan}` : ""} · requested {new Date(latest.created_at).toLocaleDateString()}
          </span>
        </div>
      )}

      {open && (
        <form onSubmit={submit} className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="tax-cert-form">
          <input data-testid="tax-cert-name" required minLength={2} placeholder="Full name (as per ID)"
            value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            onBlur={() => setForm((f) => ({ ...f, full_name: titleCase(f.full_name) }))}
            className="w-full border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-4 py-3 text-sm" />
          <input data-testid="tax-cert-aadhaar" required inputMode="numeric" minLength={12} maxLength={12}
            placeholder="Aadhaar number (required)"
            value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value.replace(/\D/g, "").slice(0, 12) })}
            className="w-full border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-4 py-3 text-sm" />
          <input data-testid="tax-cert-pan" placeholder="PAN (optional, e.g. ABCDE1234F)" maxLength={10}
            value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
            className="w-full border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-4 py-3 text-sm uppercase" />
          <textarea data-testid="tax-cert-address" required minLength={10} rows={3} placeholder="Full postal address (house/flat, area, city, pincode)"
            value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="w-full sm:col-span-2 border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-4 py-3 text-sm" />
          <div className="sm:col-span-2 flex gap-3">
            <button data-testid="tax-cert-submit" type="submit" disabled={busy}
              className="bg-[#D94833] text-white px-6 py-3 text-sm font-semibold hover:bg-[#C03F2A] transition-colors disabled:opacity-60">
              {busy ? "Generating…" : "Generate certificate"}
            </button>
            <button type="button" data-testid="tax-cert-cancel" onClick={() => setOpen(false)}
              className="border border-[#041C3E] text-[#041C3E] px-6 py-3 text-sm font-semibold hover:bg-[#041C3E] hover:text-white transition-colors">
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default function Dashboard() {
  const { user, loading, setUser, logout } = useClientAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user && !user.needs_phone) {
      clientApi.get("/client/overview").then((r) => setOverview(r.data)).catch(() => setOverview({ donations: [], assessments: [], sessions: [] }));
    }
  }, [user]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#041C3E] text-white" data-testid="client-dashboard-loading">
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }

  if (user.needs_phone) {
    return <PhoneCapture onSaved={setUser} />;
  }

  const firstName = (user.name || "there").split(" ")[0];

  return (
    <div className="min-h-screen bg-[#F7F5F2]" data-testid="client-dashboard">
      <header className="bg-[#041C3E] text-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-head font-black">The Next Move<span className="text-[#D94833]"> by Rhellex</span></Link>
          <div className="flex items-center gap-4">
            {user.picture && (
              <img data-testid="client-avatar" src={user.picture} alt={user.name} className="w-9 h-9 rounded-full border border-white/20" referrerPolicy="no-referrer" />
            )}
            <button data-testid="client-logout" onClick={async () => { await logout(); navigate("/login"); }}
              className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors">
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">Your client space</p>
          <h1 data-testid="client-welcome" className="font-head text-4xl sm:text-5xl font-black mt-3 text-[#041C3E]">
            Welcome, {firstName}.
          </h1>
          <p className="text-sm text-[#3A4A63] mt-3 max-w-2xl leading-relaxed">
            This is your home base. As Rhea onboards you, your sessions, receipts, and career assessment will appear here.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          {overview && overview.sessions.length > 0 ? (
            <DataCard testId="client-card-sessions" icon={Calendar} title="Your sessions">
              {overview.sessions.map((s, i) => (
                <div key={i} className="text-sm border-b border-[#F3F4F6] pb-2 last:border-0">
                  <p className="font-semibold">{s.package}</p>
                  <p className="text-xs text-[#9CA3AF]">{fmtDate(s.start)} · {s.status}</p>
                  {s.meet_link && <a href={s.meet_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-[#D94833] mt-1">Join link <ExternalLink size={12} /></a>}
                </div>
              ))}
            </DataCard>
          ) : (
            <PlaceholderCard testId="client-card-sessions" icon={Calendar} title="Your sessions"
              body="Once you schedule a session with Rhea, it will appear here with the joining link." />
          )}

          {overview && overview.donations.length > 0 ? (
            <DataCard testId="client-card-receipts" icon={Receipt} title="Donations & receipts">
              {overview.donations.map((d, i) => (
                <div key={i} className="text-sm border-b border-[#F3F4F6] pb-2 last:border-0">
                  <p className="font-semibold">{d.amount} · {d.package}</p>
                  <p className="text-xs text-[#9CA3AF]">{fmtDate(d.date)}</p>
                  <p className="text-[10px] text-[#9CA3AF] break-all">Ref: {d.reference_id}</p>
                </div>
              ))}
            </DataCard>
          ) : (
            <PlaceholderCard testId="client-card-receipts" icon={Receipt} title="Donations & receipts"
              body="Your donation receipts and package details will be available here." />
          )}

          {overview && overview.assessments.length > 0 ? (
            <DataCard testId="client-card-assessment" icon={ClipboardList} title="Career assessment">
              {overview.assessments.map((a, i) => (
                <div key={i} className="text-sm border-b border-[#F3F4F6] pb-2 last:border-0">
                  <p className="font-semibold">{a.package}</p>
                  {a.completed ? (
                    <p className="text-xs text-green-700 font-semibold">Completed</p>
                  ) : (
                    <>
                      <a data-testid={`resume-assessment-${a.intake_token}`} href={`/intake/${a.intake_token}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#D94833] mt-1">
                        {a.has_draft ? "Resume your assessment" : "Start your assessment"} <ExternalLink size={12} />
                      </a>
                      {a.has_draft && (
                        <p className="text-[10px] text-[#9CA3AF] mt-0.5">
                          In progress · saved {a.draft_updated_at ? fmtDate(a.draft_updated_at) : "recently"}
                        </p>
                      )}
                    </>
                  )}
                </div>
              ))}
            </DataCard>
          ) : (
            <PlaceholderCard testId="client-card-assessment" icon={ClipboardList} title="Career assessment"
              body="Your Career Discovery Assessment and six dimension scorecard will live here." />
          )}
        </div>

        <div className="mt-10 border border-[#E5E7EB] bg-white p-6" data-testid="client-account">
          <h3 className="font-head text-lg font-bold">Account details</h3>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div><p className="text-xs uppercase tracking-[0.12em] text-[#9CA3AF]">Name</p><p className="mt-1">{user.name || "—"}</p></div>
            <div><p className="text-xs uppercase tracking-[0.12em] text-[#9CA3AF]">Email</p><p className="mt-1 break-all">{user.email}</p></div>
            <div><p className="text-xs uppercase tracking-[0.12em] text-[#9CA3AF]">Phone</p><p className="mt-1">{user.phone || "—"}</p></div>
          </div>
        </div>

        <TaxCertRequest />
      </main>
    </div>
  );
}
